import DOMPurify from "dompurify";
import { motion } from "motion/react";
import type { ChatMessage } from "../hooks/useChat";
import { TypewriterHTML } from "./TypewriterHTML";

interface MessageProps {
  message: ChatMessage;
  isLatest: boolean;
  onType?: () => void;
  onTypingComplete?: () => void;
}

export function Message({
  message,
  isLatest,
  onType,
  onTypingComplete,
}: MessageProps) {
  const isUser = message.role === "user";

  // Sanitize assistant messages as they can contain HTML. User messages are rendered as plain text.
  const sanitizedContent = !isUser
    ? DOMPurify.sanitize(message.content, {
        ALLOWED_TAGS: ["b", "br", "ul", "li"],
        ALLOWED_ATTR: [],
      })
    : message.content;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{
        duration: 0.4,
        ease: [0.25, 0.46, 0.45, 0.94],
      }}
      className={`flex ${isUser ? "justify-end" : "justify-start"} mb-4`}
    >
      <div
        className={`
          relative max-w-[85%] md:max-w-[70%] px-5 py-4 rounded-2xl
          ${
            isUser
              ? "bg-linear-to-br from-emerald-600 rounded-br-md shadow-emerald-900/20 shadow-lg text-white to-teal-700"
              : "backdrop-blur-sm bg-white/80 border border-stone-200/50 rounded-bl-md shadow-md shadow-stone-900/5 text-stone-800"
          }
        `}
      >
        {!isUser && (
          <div className="flex gap-2 items-center mb-2">
            <motion.div
              animate={
                isLatest
                  ? {
                      scale: [1, 1.1, 1],
                      opacity: [0.8, 1, 0.8],
                    }
                  : {}
              }
              transition={{
                duration: 3,
                repeat: Number.POSITIVE_INFINITY,
                ease: "easeInOut",
              }}
              className="bg-linear-to-r from-emerald-400 h-2 rounded-full to-teal-500 w-2"
            />
            <span className="font-semibold text-emerald-700 text-xs tracking-wide uppercase">
              Disha
            </span>
          </div>
        )}

        {isUser ? (
          <p className="leading-relaxed text-[15px] whitespace-pre-wrap">
            {message.content}
          </p>
        ) : isLatest ? (
          <TypewriterHTML
            key={message.id}
            html={sanitizedContent}
            speed={5}
            className="leading-relaxed message-content text-[15px]"
            onType={onType}
            onComplete={onTypingComplete}
          />
        ) : (
          <div
            className="leading-relaxed message-content text-[15px]"
            // biome-ignore lint/security/noDangerouslySetInnerHtml: Sanitized with DOMPurify above
            dangerouslySetInnerHTML={{ __html: sanitizedContent }}
          />
        )}

        <div
          className={`
            mt-2 text-[11px] opacity-50
            ${isUser ? "text-emerald-100 text-right" : "text-stone-500"}
          `}
        >
          {message.timestamp.toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          })}
        </div>
      </div>
    </motion.div>
  );
}
